/* global YT */
import Flickity from 'flickity-imagesloaded';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import {
	testCSSFeature,
	getRTL,
	tabIndexManager,
	getPrevArrayIndex,
	getNextArrayIndex,
} from './_utils';
import { ytReady } from './youtube';
import _debounce from 'lodash/debounce';
import triggerAnalyticsEvent, { attachYouTubeAnalyticsEvents } from '../analytics/analytics';

const galleryClassName = 'Gallery';
const asyncClassName = 'Gallery--async';
const overlayClassName = `${galleryClassName}-overlay`;
const slideClassName = `${galleryClassName}-slide`;
const listClassName = `${galleryClassName}-list`;
const overlayShownClassName = `${overlayClassName}--shown`;
const buttonClassName = `${galleryClassName}-button`;
const closeButtonClassName = `${galleryClassName}-closeButton`;
const prevButtonClassName = `${galleryClassName}-prevButton`;
const nextButtonClassName = `${galleryClassName}-nextButton`;
const paginationClassName = `${galleryClassName}-pagination`;
const currentPageNumberClassName = `${galleryClassName}-currentPageNumber`;
const totalPagesClassName = `${galleryClassName}-totalPages`;
const wrapperClassName = `${galleryClassName}Item-wrapper`;
const tallerWrapperClassName = `${wrapperClassName}--taller`;
const thirdPartyVideoClassName = 'ThirdPartyVideo';
const thirdPartyVideoClass = `.${thirdPartyVideoClassName}`;
const widerVideoClassName = `${thirdPartyVideoClassName}--wider`;
const tallerVideoClassName = `${thirdPartyVideoClassName}--taller`;
const youtubeVideoClass = `${thirdPartyVideoClass}--youtube`;
const youkuVideoClass = `${thirdPartyVideoClass}--youku`;
const vidyardVideoClass = `${thirdPartyVideoClass}--vidyard`;
const imageClassName = 'Image-img';
const imageClass = `.${imageClassName}`;
const tallerImageClassName = `${imageClassName}--taller`;
const widerImageClassName = `${imageClassName}--wider`;
const selectedGalleryItemClassName = 'Gallery-item.is-selected';
const galleryItemClassName = 'GalleryItem';
const galleryItemIsAutoPlayClassName = `${galleryItemClassName}--autoplay`;
const videoAspectRatio = 16 / 9;
// List - Interactive Elements
let bodyInteractiveElements = null;

// Either playing or buffering
const youtubePlayingStates = [1, 3];

const youtubePlayers = {};

let previouslyFocused;

/**
 * Initiates the Flickity carousel. Adds pagination and events
 * @param  {Element} element 	The HTML element that contains the slides
 * @param  {jQuery}  overlay  The jQuery object for the overlay
 * @return {object}  		  The flickity object
 */
function initFlickity(element, overlay) {
	if ($(element).hasClass('flickity-enabled')) {
		return;
	}

	const pagination = overlay.find(`.${paginationClassName}`);
	const prevButton = overlay.find(`.${prevButtonClassName}`);
	const nextButton = overlay.find(`.${nextButtonClassName}`);

	const flickityInstance = new Flickity(element, {
		cellAlign: 'center',
		pageDots: false,
		prevNextButtons: false,
		rightToLeft: getRTL(),
		imagesLoaded: true,
		wrapAround: true,
	});

	element.removeAttribute('tabIndex');
	const slidesLength = flickityInstance.cells.length;

	//need to track whether it has settled because 'select' is called a bunch
	//before the gallery is even opening.
	let hasSettled = false;

	if (slidesLength > 1) {
		overlay.addClass(`${overlayClassName}--paginated`);
		const totalPages = pagination.children(`.${totalPagesClassName}`);
		const currentPageNumber = pagination.children(`.${currentPageNumberClassName}`);
		currentPageNumber.text(flickityInstance.selectedIndex + 1);
		totalPages.text(slidesLength);

		flickityInstance.on('select', () => {
			const cells = flickityInstance.getCellElements();
			const prevIndex = getPrevArrayIndex(cells, flickityInstance.selectedIndex);
			const nextIndex = getNextArrayIndex(cells, flickityInstance.selectedIndex);

			cells.forEach((cell, index) => {
				const $cell = $(cell);
				$cell.find(`${thirdPartyVideoClass}, iframe`).attr('tabindex', -1);
				// Pause/Stop the video if there was one in the previously selected cell
				if (index === prevIndex || index === nextIndex) {
					const cellHasYoutubeVideo =
						$cell.find(`.${thirdPartyVideoClassName}--youtube`).length > 0;
					const cellHasYoukuVideo =
						$cell.find(`.${thirdPartyVideoClassName}--youku`).length > 0;

					if (youtubePlayers && cellHasYoutubeVideo) {
						pauseYoutube(cell);
					} else if (cellHasYoukuVideo) {
						resetYouku($cell);
					}
				} else if (index === flickityInstance.selectedIndex) {
					if (hasSettled) {
						triggerAnalyticsEvent('gallery.transition', {
							element: overlay,
							cell: $cell,
						});
					}

					checkForAutoPlay(element);
				}
			});

			flickityInstance.on('settle', () => {
				hasSettled = true;
			});

			$(flickityInstance.selectedCell.element).find('iframe').removeAttr('tabindex');
			$(flickityInstance.selectedCell.element).find(thirdPartyVideoClass).attr('tabindex', 0);

			currentPageNumber.text(flickityInstance.selectedIndex + 1);
		});

		prevButton.on('click.gallery', () => flickityInstance.previous(true));
		nextButton.on('click.gallery', () => flickityInstance.next(true));
	} else {
		flickityInstance.unbindDrag();
	}

	return flickityInstance;
}

function getActiveSlide(container) {
	const $container = $(container);
	if ($container) {
		const activeSlide = $container.find(`.${selectedGalleryItemClassName}`);

		return activeSlide;
	}

	return false;
}

function checkForAutoPlay(element) {
	const activeSlide = getActiveSlide(element);
	const activeGalleryItem = activeSlide.find('.GalleryItem');

	if (activeGalleryItem.hasClass(galleryItemIsAutoPlayClassName)) {
		const youtubeContainer = activeSlide.find(thirdPartyVideoClass);
		playYoutube(youtubeContainer);
	}
}

function initIframes(container) {
	$(container)
		.find('iframe')
		.each((i, iframe) => {
			const $iframe = $(iframe);
			$iframe.attr('src', $iframe.data('src'));
		});
}

/**
 * Fix that adds behaviour similar to "object-fit: contain" to the images
 * @param  {jQuery} list  jQuery object for the element that contains the slides
 * @return {undefined}
 */
function ieContainFix(list) {
	const containers = list.find(`.${slideClassName}`);
	const firstContainer = containers.first();
	const containerAspectRatio = firstContainer.width() / firstContainer.height();

	containers.each((index, element) => {
		const container = $(element);
		const img = container.find(imageClass);

		if (img.length > 0) {
			const image = img.get(0);
			img.addClass(`${imageClassName}--ieFixed`);
			img.removeClass(`${widerImageClassName} ${tallerImageClassName}`);

			const imgAspectRatio = image.naturalWidth / image.naturalHeight;

			// The image is wider than its container
			if (imgAspectRatio > containerAspectRatio) {
				img.addClass(widerImageClassName);
				container.find(`.${wrapperClassName}`).addClass(tallerWrapperClassName);
			}

			// The image is taller than its container
			if (imgAspectRatio < containerAspectRatio) {
				container.find(`.${wrapperClassName}`).removeClass(tallerWrapperClassName);
				img.addClass(tallerImageClassName);
			}
		}
	});
}

function initializeYoutube($container, overlay) {
	$container.find(youtubeVideoClass).each((i, element) => {
		const $element = $(element);
		const $div = $element.find('div');
		const divId = $div.attr('id');
		const videoId = $div.data('video-id');

		ytReady(() => {
			youtubePlayers[divId] = new YT.Player(divId, {
				host: 'https://www.youtube-nocookie.com',
				playerVars: {
					enablejsapi: 1,
					origin: document.domain,
					rel: 0,
				},
				width: '100%',
				height: '100%',
				videoId: videoId,
				events: {
					onReady: e => {
						attachYouTubeAnalyticsEvents(e);
						checkForAutoPlay(overlay);
					},
				},
			});
		});
	});
}

function fixVideo(list) {
	const firstContainer = list.find(`.${slideClassName}`).first();
	const containerAspectRatio = firstContainer.width() / firstContainer.height();
	const videos = list.find(thirdPartyVideoClass);
	videos.removeClass(`${tallerVideoClassName} ${widerVideoClassName}`);
	list.find(`.${wrapperClassName}`).removeClass(tallerWrapperClassName);

	// The video is wider than its container
	if (videoAspectRatio > containerAspectRatio) {
		list.find(`.${wrapperClassName}`).addClass(tallerWrapperClassName);
		videos.addClass(widerVideoClassName);
		videos.css({
			height: firstContainer.width() * (1 / videoAspectRatio),
			width: '100%',
		});
	}

	// The video is taller than its container
	if (videoAspectRatio < containerAspectRatio) {
		videos.addClass(tallerVideoClassName);
		videos.css({
			width: firstContainer.height() * videoAspectRatio,
			height: '100%',
		});
	}
}

function resetYouku(container) {
	$(container)
		.find(youkuVideoClass)
		.each((i, element) => {
			const $clone = $(element).clone();
			$(element).replaceWith($clone);
		});
}

function playYoutube(container) {
	const $iframe = $(container).find('iframe');
	const player = youtubePlayers[$iframe.attr('id')];
	player.playVideo();
}

function pauseYoutube(container) {
	const $iframe = $(container).find('iframe');
	const player = youtubePlayers[$iframe.attr('id')];
	// The player may not have been initialized
	const playerState = player.getPlayerState ? player.getPlayerState() : -1;
	if (playerState in youtubePlayingStates) {
		player.pauseVideo();
	}
}

function stopYoutube(container) {
	$(container)
		.find(youtubeVideoClass)
		.each((i, element) => {
			const iframe = $(element).find('iframe');
			const player = youtubePlayers[iframe.attr('id')];
			player.stopVideo();
		});
}

function stopVidyard(overlay) {
	const vidyardVideos = overlay.find(vidyardVideoClass);

	vidyardVideos.each((index, vidyardVideo) => {
		const vidyardPlayerContainer = $(vidyardVideo).find('.vidyard-player-container');
		const uuid = vidyardPlayerContainer.attr('uuid');

		if (uuid) {
			const players = VidyardV4.api.getPlayersByUUID(uuid);

			players.forEach(player => {
				player.pause();
			});
		}
	});
}

/**
 * Opens an overlay, prevents scroll in the body, prevents tabbing in Interactive
 * elements that don't belong to the overlay and focuses on the overlay at the end
 * @param  {jQuery} overlay 					The jQuery object for the overlay to show
 * @param  {object} flickityInstance 	The flickity object
 * @return {object}										The flickity object
 */
export function openOverlay(overlay, flickityInstance, eventTarget) {
	if (!overlay.hasClass(overlayShownClassName)) {
		// If the gallery hasn't been initialized, initialize the frames
		if (!overlay.hasClass('flickity-enabled')) {
			initIframes(overlay);
		}

		const $list = overlay.find(`.${listClassName}`);
		const list = $list.get(0);
		const hasVideos = !!($list.find(thirdPartyVideoClass).length > 0);

		if (!flickityInstance) {
			if (hasVideos) {
				initializeYoutube($list, overlay);
			}
			flickityInstance = initFlickity(list, overlay);
		} else {
			// Select the first one
			flickityInstance.select(0, undefined, true);
		}

		overlay.addClass(overlayShownClassName);
		bodyInteractiveElements.each((_, element) => tabIndexManager.disable(element));

		previouslyFocused = eventTarget;

		restartHeight($list);
		$(window).on(
			'resize.gallery',
			_debounce(() => restartHeight($list), 100)
		);

		if (hasVideos) {
			fixVideo($list);
			$(window).on(
				'resize.gallery',
				_debounce(() => fixVideo($list), 100)
			);
		}

		// Wait until the elements have been rendered completely (ie. after the animation)
		setTimeout(() => {
			$list.first().focus();
			disableBodyScroll();
		}, 800);

		triggerAnalyticsEvent('gallery.open', { element: overlay });
	}

	return flickityInstance;
}

/**
 * Closes an overlay returning everything to normal (focus, scroll, tabbing)
 * @param  {jQuery} overlay    The jQuery object of the overlay to close
 * @param  {jQuery} button  	 The jQuery object for the button that opened the overlay
 * @return {undefined}
 */
function closeOverlay(overlay, button) {
	if (overlay.hasClass(overlayShownClassName)) {
		overlay.removeClass(overlayShownClassName);
		resetYouku(overlay);
		stopYoutube(overlay);
		stopVidyard(overlay);
		enableBodyScroll();
		$(window).off('resize.gallery');
		bodyInteractiveElements.each((_, element) => tabIndexManager.reinstate(element));
		previouslyFocused ? $(previouslyFocused).focus() : button.focus();
		previouslyFocused = null;
	}
}

/**
 * Makes the gallery have the height of the viewport.
 * This fixes devices in which 100vh !== $(window).height()
 * @param  {jQuery} container    The gallery to change
 * @return {undefined}
 */
function restartHeight(container) {
	container.find(`.${slideClassName}`).each((index, element) => {
		$(element).css('height', `${$(window).height()}px`);
	});
}

/**
 * Initializes a gallery using Flickity
 * @param {HTMLElement|jQuery}	element		The gallery element with class .Gallery
 * @return {Object}												An object that contains the flickity instance for the initialized gallery
 */
export function initSingleGallery(element) {
	const gallery = $(element);
	const overlay = gallery.children(`.${overlayClassName}`);
	const button = gallery.children(`.${buttonClassName}`);
	const closeButton = overlay.children(`.${closeButtonClassName}`);

	// Creates an object that can be passed by reference, so that its keys can be
	// overwriten without losing the reference
	let flickity = {};

	button.on('click', () => {
		const _flickity = openOverlay(overlay, flickity.instance);
		flickity.instance = _flickity ? _flickity : flickity.instance;
	});
	closeButton.on('click', () => closeOverlay(overlay, button));
	$(window).on('keyup', event => {
		const escapePressed = event.which === 27;
		if (escapePressed) {
			closeOverlay(overlay, button);
		}
	});

	return flickity;
}

/**
 * Initialize the interactive elements of the Dom that are not from the Overlay Gallery.
 * @return {undefined}
 */
export const initBodyInteractiveElements = (container = window.document) => {
	const $container = $(container);
	bodyInteractiveElements = $container
		.find('a,button,link,input,iframe,select,[tabIndex][tabIndex!=-1]')
		.filter(':not(.Gallery-overlay *)');
};

/**
 * Initiates all the galleries, adding the appropriate event listeners
 * @return {undefined}
 */
export default function initGalleries(container = window.document) {
	const $container = $(container);
	initBodyInteractiveElements(container);

	$container
		.find(`.${galleryClassName}`)
		.filter(`:not(.${asyncClassName})`)
		.each((_, element) => initSingleGallery(element));

	let flickity = {};

	window.addEventListener('openGalleryOverlay', event => {
		if (event?.detail?.id) {
			const overlay = $(`#${event.detail.id} .${overlayClassName}`);
			if (overlay.length) {
				const _flickity = openOverlay(overlay, flickity.instance);
				flickity.instance = _flickity ? _flickity : flickity.instance;

				if (event.detail.slideIndex) {
					flickity.instance.select(event.detail.slideIndex, undefined, true);
				}
			}
		}
	});
}
